import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { reducedMotion, useScene } from "../store";
import { bodyLive } from "./bodyState";
import { frame } from "./frame";
import { displayRadius } from "./sceneData";
import type { BodyId } from "@solar/ephemeris";

/** 默认全景：从黄道北侧斜上方俯视。模型模式海王星轨道（≈100）及冥王星入画；真实比例下冥王星在 ≈131，需要拉远。 */
const HOME = { yaw: 0.55, pitch: 0.62, dist: 340, distReal: 640 };
const MAX_DIST = 2500;
const homeDist = (real: boolean) => (real ? HOME.distReal : HOME.dist);
/** 聚焦某天体时的相机距离（场景单位）。真实比例下聚焦地球会把月球轨道也留在画面里（地球仅数像素，滚轮可继续放大）。 */
function focusDist(id: BodyId, real: boolean): number {
  const r = displayRadius(id, real ? 1 : 0);
  if (real && (id === "earth")) return 0.03;
  return Math.max(r * (real ? 9 : 9), real ? 1e-5 : 6);
}
const minDistFor = (sel: BodyId | null, real: boolean) => (real ? (sel ? displayRadius(sel, 1) * 1.15 : 1e-4) : 1.5);

/**
 * 自写相机控制器（ADR-0001：不用 OrbitControls，为 S3 的浮动原点留余地）。
 * 鼠标：左键旋转，右键/Shift+左键平移，滚轮缩放（对数）；触摸：单指旋转，双指缩放+平移。
 * 键盘：WASD 移动，QE 升降，方向键旋转，+/- 缩放，F 聚焦，R 全景，T 今天，空格播放，Esc 取消选中。
 * 聚焦时以平滑偏移跟随天体，目标始终是天体中心（相对原点的偏移在 float64 里做，GPU 只看相机相对坐标，见 S3）。
 */
export function CameraRig() {
  const { camera, gl, size } = useThree();
  const st = useRef({
    yaw: HOME.yaw, pitch: HOME.pitch, dist: HOME.dist, distT: HOME.dist,
    target: new THREE.Vector3(), off: new THREE.Vector3(), free: new THREE.Vector3(), // free：未聚焦时的手动平移
    keys: new Set<string>(), idle: 0, lastSel: null as string | null, lastReset: 0, lastMode: "model" as string, minDist: 1.5,
  });
  const fill = useRef<THREE.DirectionalLight>(null);

  useEffect(() => {
    const el = gl.domElement, S = st.current;
    const ptrs = new Map<number, { x: number; y: number }>();
    let moved = 0, mode: "rot" | "pan" | null = null, pinch = 0;
    const down = (e: PointerEvent) => {
      el.setPointerCapture(e.pointerId); ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY }); moved = 0;
      mode = e.button === 2 || e.shiftKey ? "pan" : "rot"; S.idle = 0;
      if (ptrs.size === 2) { const [a, b] = [...ptrs.values()]; pinch = Math.hypot(a!.x - b!.x, a!.y - b!.y); }
    };
    const move = (e: PointerEvent) => {
      const p = ptrs.get(e.pointerId); if (!p) return;
      const dx = e.clientX - p.x, dy = e.clientY - p.y; p.x = e.clientX; p.y = e.clientY; moved += Math.abs(dx) + Math.abs(dy); S.idle = 0;
      if (ptrs.size === 2) {
        const [a, b] = [...ptrs.values()]; const d = Math.hypot(a!.x - b!.x, a!.y - b!.y);
        if (pinch) S.distT = clampD(S.distT * (pinch / d)); pinch = d; pan(dx / 2, dy / 2); return;
      }
      if (mode === "pan") pan(dx, dy);
      else { S.yaw += dx * 0.006; S.pitch = Math.max(-1.5, Math.min(1.5, S.pitch + dy * 0.005)); } // 向右拖：近侧向右转
    };
    const up = (e: PointerEvent) => { ptrs.delete(e.pointerId); if (ptrs.size < 2) pinch = 0; };
    const wheel = (e: WheelEvent) => { e.preventDefault(); S.distT = clampD(S.distT * Math.exp(e.deltaY * 0.0016)); S.idle = 0; };
    const ctx = (e: Event) => e.preventDefault();
    const pan = (dx: number, dy: number) => {
      const k = S.dist * 0.0016, right = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 0), upv = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 1);
      S.free.addScaledVector(right, -dx * k).addScaledVector(upv, dy * k);
    };
    const key = (e: KeyboardEvent, isDown: boolean) => {
      const t = e.target as HTMLElement; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      const k = e.key.toLowerCase();
      if (isDown) {
        const sc = useScene.getState();
        if (k === " ") { e.preventDefault(); sc.setPlaying(!sc.playing); return; }
        if (k === "t") { sc.setTimeMs(Date.now()); return; }
        if (k === "r") { sc.resetView(); return; }
        if (k === "escape") { sc.select(null); return; }
        if (k === "f" && sc.selected) { S.lastSel = null; return; }
        if (k === "+" || k === "=") S.distT = clampD(S.distT / 1.25);
        if (k === "-") S.distT = clampD(S.distT * 1.25);
        S.keys.add(k);
      } else S.keys.delete(k);
    };
    const kd = (e: KeyboardEvent) => key(e, true), ku = (e: KeyboardEvent) => key(e, false);
    el.addEventListener("pointerdown", down); el.addEventListener("pointermove", move); el.addEventListener("pointerup", up); el.addEventListener("pointercancel", up);
    el.addEventListener("wheel", wheel, { passive: false }); el.addEventListener("contextmenu", ctx);
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => {
      el.removeEventListener("pointerdown", down); el.removeEventListener("pointermove", move); el.removeEventListener("pointerup", up); el.removeEventListener("pointercancel", up);
      el.removeEventListener("wheel", wheel); el.removeEventListener("contextmenu", ctx);
      window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku);
    };
  }, [camera, gl]);

  useFrame((_, dt) => {
    const S = st.current, sc = useScene.getState();
    const real = sc.mode === "real";
    // 回到全景
    if (sc.resetNonce !== S.lastReset) { S.lastReset = sc.resetNonce; S.yaw = HOME.yaw; S.pitch = HOME.pitch; S.distT = homeDist(real); S.free.set(0, 0, 0); S.off.copy(S.target); S.lastSel = null; }
    // 选中变化：设置缩放预设，并把“当前目标 − 新天体”记为偏移，之后平滑衰减 → 飞行效果
    const sel = sc.selected;
    minDistNow = minDistFor(sel, real);
    if (sel !== S.lastSel) {
      S.lastSel = sel;
      const live = sel ? bodyLive.get(sel) : null;
      if (live) { S.off.copy(S.target).sub(live.world); S.free.set(0, 0, 0); S.distT = clampD(focusDist(sel!, real)); }
      else if (!sel) { S.off.copy(S.target); S.free.set(0, 0, 0); S.distT = clampD(homeDist(real)); }
    }
    // 切换模式：相机距离随之换成新模式下的预设（聚焦天体 / 全景）
    if (sc.mode !== S.lastMode) { S.lastMode = sc.mode; S.distT = clampD(sel ? focusDist(sel, real) : homeDist(real)); }
    // 键盘平移/旋转
    const k = S.keys, sp = S.dist * 0.9 * dt;
    if (k.size) {
      const fwd = new THREE.Vector3(-Math.sin(S.yaw), 0, -Math.cos(S.yaw)), right = new THREE.Vector3(Math.cos(S.yaw), 0, -Math.sin(S.yaw));
      if (k.has("w")) S.free.addScaledVector(fwd, sp); if (k.has("s")) S.free.addScaledVector(fwd, -sp);
      if (k.has("a")) S.free.addScaledVector(right, -sp); if (k.has("d")) S.free.addScaledVector(right, sp);
      if (k.has("e")) S.free.y += sp; if (k.has("q")) S.free.y -= sp;
      if (k.has("arrowleft")) S.yaw -= 1.4 * dt; if (k.has("arrowright")) S.yaw += 1.4 * dt;
      if (k.has("arrowup")) S.pitch = Math.min(1.5, S.pitch + 1.0 * dt); if (k.has("arrowdown")) S.pitch = Math.max(-1.5, S.pitch - 1.0 * dt);
      if (k.size) S.idle = 0;
    }
    // 自动环绕（默认关）
    S.idle += dt; if (sc.layers.auto && !reducedMotion && S.idle > 2.5) S.yaw += dt * 0.045;
    // 目标（绝对显示坐标，float64）= 聚焦天体 + 衰减中的偏移 + 手动平移；它同时就是浮动原点
    const live = sel ? bodyLive.get(sel) : null;
    S.off.multiplyScalar(Math.exp(-dt * (reducedMotion ? 80 : 5)));
    if (live) S.target.copy(live.world).add(S.off).add(S.free);
    else S.target.copy(S.free).add(S.off);
    frame.origin.copy(S.target);
    S.dist = Math.exp(Math.log(S.dist) + (Math.log(S.distT) - Math.log(S.dist)) * (1 - Math.exp(-dt * 5)));
    frame.dist = S.dist;
    frame.pxPerUnit = (size.height / 2) / (S.dist * Math.tan((((camera as THREE.PerspectiveCamera).fov) * Math.PI) / 360));
    const cp = Math.cos(S.pitch), spch = Math.sin(S.pitch);
    // 相机相对原点放置：数值始终很小，不论缩放到多近
    camera.position.set(Math.sin(S.yaw) * cp * S.dist, spch * S.dist, Math.cos(S.yaw) * cp * S.dist);
    camera.lookAt(0, 0, 0);
    const pc = camera as THREE.PerspectiveCamera;
    pc.near = Math.max(1e-8, S.dist * 1e-3); pc.far = 40000; pc.updateProjectionMatrix();
    frame.sunRel.copy(frame.origin).negate(); // 太阳在绝对原点
    // 模型模式的“补光”：跟随相机的弱方向光，让背光面仍能看清图案（真实比例模式将关闭，S3）
    if (fill.current) { fill.current.position.copy(camera.position); fill.current.target.position.set(0, 0, 0); fill.current.target.updateMatrixWorld(); fill.current.intensity = 0.9 * (1 - frame.m) + 0.02 * frame.m; }
  });
  return <directionalLight ref={fill} intensity={0.9} />;
}
let minDistNow = 1.5;
const clampD = (d: number) => Math.min(MAX_DIST, Math.max(minDistNow, d));
